import { Lock, Plus, RefreshCw, SearchX, Shield } from "lucide-react";
import { useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { RoleEditorDrawer } from "@/modules/dashboard/settings/components/access/RoleEditorDrawer";
import { useApiKeysData, usePoliciesData } from "@/modules/dashboard/settings/hooks/useAccessData";
import { useRoleMembers } from "@/modules/dashboard/settings/hooks/useRoleMembers";
import type { AccessRole } from "@/modules/dashboard/settings/types/access.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { useTranslations } from "@/shared/lang/useTranslations";
import { toastApiError } from "@/shared/utils/toast";

import { AccessCatalogToolbar } from "../components/AccessCatalogToolbar";
import { RolesCards } from "../components/RolesCards";
import { RolesKpiStrip } from "../components/RolesKpiStrip";
import { RolesTable } from "../components/RolesTable";
import { useRolesCatalog } from "../hooks/useRolesCatalog";

/**
 * `/app/access/roles` — the roles catalog for Coloca staff.
 *
 * A role is the only thing the backend links to a principal: policies reach a person solely through one, and
 * there is no endpoint to grant a loose permission. That is why this is the landing surface of the module,
 * and why assigning one to somebody stays in `/app/users`.
 *
 * The editor is `RoleEditorDrawer`, imported from Settings unchanged — the same drawer an organization uses
 * for the same catalog. One implementation, two entry points, so the two can never answer differently.
 */
const RolesPage = () => {
  const { t } = useTranslations("settings");
  const { t: tGlobal } = useTranslations("global");
  const { isInternalUser } = useAuth();

  const catalog = useRolesCatalog();
  const { policies } = usePoliciesData();
  const { load: loadKeys } = useApiKeysData();

  const [editor, setEditor] = useState<{ role: AccessRole | null } | null>(null);

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_gestion") },
        { label: tGlobal("sidebar_menu_roles"), to: "/app/access/roles" },
      ],
      [tGlobal],
    ),
  );

  const editorRole = editor?.role ?? null;
  // Creating is never read-only; editing is, unless the caller may write AND the role itself is writable.
  const editorReadOnly = editorRole ? !catalog.canUpdate || !catalog.isEditable(editorRole) : false;
  // Fetched only while the editor is open, and only for users — an API key holding the role cannot be listed
  // at all (`GET /api-keys` takes no `role_id`), which the drawer states rather than hides.
  const memberPreview = useRoleMembers(editorRole?.id ?? null);

  /**
   * `cascaded` is only true for a DELETE: the backend unassigns the role from every principal in the same
   * transaction, so the API Keys view — which shares this module's catalog — would keep presenting pre-delete
   * assignments as authoritative. Renaming a role cascades nothing, and reloading the keys there would cost
   * one request per key for no change.
   */
  const reload = (cascaded = false) => {
    catalog.reload().catch(toastApiError);
    if (cascaded) loadKeys().catch(toastApiError);
  };

  if (catalog.hasNoAccess) return <NoAccessView />;

  const empty = catalog.hasActiveFilters ? (
    <ColocaEmpty
      icon={SearchX}
      title={t("access_roles_empty_filtered_title")}
      description={t("access_page_empty_filtered_body")}
      surface="card"
    >
      <ColocaButton variant="secondary" size="sm" onClick={catalog.clearFilters}>
        {t("access_page_clear_filters")}
      </ColocaButton>
    </ColocaEmpty>
  ) : (
    <ColocaEmpty
      icon={Shield}
      title={t("access_roles_empty_title")}
      description={t("access_roles_empty_body")}
      surface="card"
    >
      {catalog.canCreate && (
        <ColocaButton variant="primary" size="sm" icon={Plus} onClick={() => setEditor({ role: null })}>
          {t("access_roles_create")}
        </ColocaButton>
      )}
    </ColocaEmpty>
  );

  const footer =
    catalog.total > catalog.pageSize ? (
      <ColocaPagination
        page={catalog.page}
        perPage={catalog.pageSize}
        total={catalog.total}
        onPageChange={catalog.setPage}
        // The table's `footer` slot already draws the rule above the pagination.
        withSeparator={false}
      />
    ) : undefined;

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <ColocaPageHeader
        title={t("access_roles_title")}
        description={t("access_roles_sub")}
        actions={
          <>
            <ColocaButton
              variant="secondary"
              size="sm"
              icon={RefreshCw}
              // Wrapped, not passed by reference: `reload` takes a flag, and a bare handler would receive
              // the click event as `cascaded` — an object, therefore truthy, therefore a needless fan-out.
              onClick={() => reload()}
              disabled={catalog.isLoading}
            >
              {t("access_page_refresh")}
            </ColocaButton>
            {catalog.canCreate && (
              <ColocaButton variant="primary" size="sm" icon={Plus} onClick={() => setEditor({ role: null })}>
                {t("access_roles_create")}
              </ColocaButton>
            )}
          </>
        }
      />

      {/* `null` while loading: four zeros over a skeleton table would state that the catalog is empty. */}
      <RolesKpiStrip
        counts={catalog.isLoading ? null : catalog.counts}
        value={catalog.bucket}
        onChange={catalog.setBucket}
      />

      {/* Stated once, where the number is, instead of on every row: a protected role is the majority of this
          catalog, so a per-row explanation would be noise. */}
      {!catalog.canCreate && (
        <ColocaAlert variant="secondary" icon={Lock} text={t("access_roles_readonly_note")} />
      )}

      <AccessCatalogToolbar
        query={catalog.query}
        onQueryChange={catalog.setQuery}
        searchPlaceholder={t("access_roles_search_placeholder")}
        scope={catalog.scope}
        onScopeChange={catalog.setScope}
      />

      <ColocaSurface padding="none" className="overflow-hidden">
        <div className="hidden lg:block">
          <RolesTable
            rows={catalog.rows}
            loading={catalog.isLoading}
            empty={empty}
            footer={footer}
            isInternalUser={isInternalUser}
            onOpen={(row) => setEditor({ role: row.role })}
          />
        </div>
        <div className="lg:hidden">
          {catalog.rows.length === 0 && !catalog.isLoading ? (
            <div className="p-6">{empty}</div>
          ) : (
            <RolesCards
              rows={catalog.rows}
              loading={catalog.isLoading}
              isInternalUser={isInternalUser}
              onOpen={(row) => setEditor({ role: row.role })}
            />
          )}
          {footer && <div className="border-t border-border px-4 py-3">{footer}</div>}
        </div>
      </ColocaSurface>

      <RoleEditorDrawer
        open={!!editor}
        onOpenChange={(open) => {
          if (!open) setEditor(null);
        }}
        role={editorRole}
        readOnly={editorReadOnly}
        policies={policies}
        memberPreview={memberPreview}
        canDelete={editorRole ? catalog.canDelete && catalog.isEditable(editorRole) : false}
        onSaved={() => {
          setEditor(null);
          reload();
        }}
        onDeleted={() => {
          setEditor(null);
          reload(true);
        }}
      />
    </div>
  );
};

export default RolesPage;
